import { DOCUMENT_AGENT_API_BASE_URL } from "../config/constants";

export type SourceLink = {
    title: string;
    url?: string | null;
    category?: string | null;
    score?: number | null;
    documentId?: string | null;
};

// One retrieved chunk that a document-grounded answer drew on. The backend
// sends these only on turns that genuinely answered from documents —
// refusals/clarifications carry none — so `citations.length > 0` doubles as
// the "an answer was picked" signal.
export type SourceCitation = {
    documentId: string;
    documentTitle: string;
    // Sanitized original filename including extension (e.g. "spec-sheet.pdf").
    documentFileName?: string | null;
    chunkId: string;
    chunkIndex: number;
    // 1-based page for PDFs; often null for non-PDF documents.
    pageNumber?: number | null;
    sectionTitle?: string | null;
    // The retrieved chunk text (≤ 4000 chars). Model-transcribed and
    // whitespace-normalized — NOT guaranteed verbatim in the document, which
    // is why highlighting (highlighting.ts) is fuzzy rather than substring.
    quote: string;
    // true: the model explicitly cited this chunk; false: fail-open (all
    // served chunks were attached).
    modelCited: boolean;
};


export type VisualAssetReference = {
    assetId: string;
    documentId: string;
    documentTitle: string;
    documentFileName?: string | null;
    assetType: string;
    pageNumber: number;
    label?: string | null;
    retrievalDescription?: string | null;
    assetGroupKey?: string | null;
    sequenceIndex: number;
    retrievalScore: number;
    confidence: number;
    // Normalized 0..1 coordinates, origin at the rendered page's top-left.
    bboxLeft: number;
    bboxTop: number;
    bboxRight: number;
    bboxBottom: number;
    useFullPageFallback: boolean;
};

export type AgentAction = {
    label: string;
    type: string;
    value: string;
};

export type ChatResponse = {
    answer: string;
    conversationId: string;
    messageId: string;
    intent: string;
    category?: string | null;
    escalationRecommended: boolean;
    sources: SourceLink[];
    actions: AgentAction[];
    // Absent on older deployed backends — callers must treat undefined as [].
    citations?: SourceCitation[];
    // Original PDF visuals selected on the retrieval-only evidence path.
    visualAssets?: VisualAssetReference[];
};

// Which reading of the source produced the indexed chunks — mirrors the
// backend's DocumentReadingMode. Absent on older deployed backends, and
// callers must treat that the same as "vision"/"text": only "text_only" says
// something was lost.
export type DocumentReadingMode = "vision" | "text_only" | "text";

// Mirrors the backend's DocumentIngestionJobStatus. "queued" is the only
// value the upload response itself ever carries; the rest are only ever
// observed via fetchIngestionStatus's poll.
export type IngestionJobStatus = "queued" | "running" | "indexed" | "failed" | "superseded";

// The upload endpoint is asynchronous: it accepts the file and hands back a
// job id, and the indexing work that used to happen inline now happens in
// the background, tracked by fetchIngestionStatus. `jobId` is what
// distinguishes this current shape from the legacy one below — check for its
// presence, not for `indexed`.
//
// `indexed`/`indexingError`/`chunkCount`/`readingMode` are kept as optional
// LEGACY fields, not because the current backend sends them, but because an
// older deployed backend (mid-rollout, or a client that hasn't refreshed)
// still answers with a synchronous 200 carrying `indexed: true` and no
// `jobId` at all — that path must keep working exactly as it always did.
export type UploadResponse = {
    message: string;
    conversationId: string;
    fileName: string;
    storageKey: string;
    jobId?: string;
    status?: IngestionJobStatus;
    indexed?: boolean;
    indexingError?: string | null;
    chunkCount?: number | null;
    readingMode?: DocumentReadingMode | null;
};

// One row of the GET /storage/documents/status response — the durable
// outcome of one uploaded file's ingestion job. chunkCount/readingMode are
// null until a reading has actually produced them, and errorCategory/
// errorMessage are null until the job has failed.
//
// progressStage/progressDone/progressTotal are optional and additive, for the
// same reason every other field on this type that an older deployed backend
// might not send is left un-required elsewhere in this module: they are a
// DESCRIPTION of how far a reading has got, may be stale by a moment, and a
// caller must never conclude anything from them that `status` does not
// already say. Absent entirely (older backend) or present with null counts
// (a stage with no countable units, e.g. "extracting") are both legitimate —
// callers render their existing plain text for either case.
export type IngestionJob = {
    jobId: string;
    fileName: string;
    storageKey: string;
    status: IngestionJobStatus;
    chunkCount: number | null;
    readingMode: DocumentReadingMode | null;
    progressStage?: string | null;
    progressDone?: number | null;
    progressTotal?: number | null;
    errorCategory: string | null;
    errorMessage: string | null;
    attempts: number;
    createdAt: string;
    startedAt: string | null;
    completedAt: string | null;
};

type IngestionStatusResponse = {
    conversationId: string;
    jobs: IngestionJob[];
};

export type ClearResponse = {
    message: string;
    clearedConversationId: string;
    conversationId: string;
    deletedFiles: number;
    removedDocuments: number;
    removedChunks: number;
};

// One presigned destination for a single part, S3 only. `url` is a bearer
// credential for exactly that one part write — it is used to PUT the part
// and never logged, traced, or persisted anywhere on this side either.
export type DirectUploadPartUrl = {
    partNumber: number;
    url: string;
};

// POST documents/direct-uploads' 201 body: everything needed to move a file
// too large for the legacy single-request path. `partUrls` is null on the
// local provider (parts go through uploadDirectPart's own API route instead)
// and an array — one entry per part, never partial — on S3; callers route on
// its presence rather than on a provider name they are never told.
export type InitiateDirectUploadResponse = {
    uploadId: string;
    storageKey: string;
    partSizeBytes: number;
    partCount: number;
    partUrls: DirectUploadPartUrl[] | null;
    expiresAt: string;
};

// POST .../complete's 202 body: the exact Phase 1 enqueue shape
// (message/conversationId/fileName/storageKey/jobId/status) plus the
// uploadId, so a caller that already knows how to follow a small upload's
// jobId into the queued/indexing poll needs nothing new to follow a large
// one's.
export type CompleteDirectUploadResponse = {
    message: string;
    conversationId: string;
    fileName: string;
    storageKey: string;
    jobId: string;
    status: IngestionJobStatus;
    uploadId: string;
};

export type FeedbackRating = "helpful" | "not_helpful" | "wrong_source" | "needs_human";

const apiUrl = (path: string) => `${DOCUMENT_AGENT_API_BASE_URL}${path}`;

// Every request is aborted after this long so a hung backend can never leave
// the UI stuck in a permanent "uploading"/"sending" state.
// Upload no longer needs to outlive the backend's indexing work — indexing now runs in a background
// job the upload request never waits on, and its progress is tracked separately by
// fetchIngestionStatus's own poll. This timeout covers only the synchronous part of an upload: the
// multipart transfer plus the ingestion job being enqueued. It stays at 420s regardless, generous
// enough for a large file over a slow connection to finish transferring.
const UPLOAD_TIMEOUT_MS = 420_000;
const CHAT_TIMEOUT_MS = 90_000;
const CLEAR_TIMEOUT_MS = 60_000;
const FEEDBACK_TIMEOUT_MS = 30_000;
// Source-preview file download: the HEAD guard below keeps a fetched original
// under MAX_PREVIEW_FETCH_BYTES, so this is generous for that, but it still
// ends a hung stream instead of leaving the preview drawer spinning forever.
const PREVIEW_TIMEOUT_MS = 120_000;
// A status poll is a small, cheap read against the ingestion job ledger —
// nothing here should ever take long, so this stays short enough that a
// hung poll can't stack up behind the panel's ~2500ms interval.
const STATUS_TIMEOUT_MS = 15_000;
// The three control-plane calls of a large upload — opening the transfer,
// finishing it, and giving it up — are all small JSON round trips regardless
// of how big the file behind them is, so they share one short timeout.
const DIRECT_UPLOAD_CONTROL_TIMEOUT_MS = 30_000;
// One PART, not one file: generous enough for a slow connection to move a
// single part at the shipped part size, but short enough that a stalled part
// fails fast instead of holding up the retry (and the next wave) behind it.
const DIRECT_UPLOAD_PART_TIMEOUT_MS = 120_000;
// Fetching the original file for the source-preview drawer HEADs it first;
// same budget as the GET it may replace, since a HEAD that hangs must not
// leave the drawer waiting any longer than the fetch it was guarding.
const PREVIEW_HEAD_TIMEOUT_MS = PREVIEW_TIMEOUT_MS;
// Above this, fetchDocumentFile refuses the GET and answers "oversized"
// instead: a preview blob this large would hold the whole file in memory (as
// a Blob) just to render a handful of cited passages, which the passages +
// download-link fallback already does without ever fetching the bytes.
const MAX_PREVIEW_FETCH_BYTES = 100_000_000;

const MAX_ERROR_BODY_LENGTH = 200;
const GENERIC_ERROR_PREFIX = "The service returned status";

// Falls back to the backend's own default UploadPolicy window (see
// RateLimitingOptions/Program.cs) whenever the Retry-After header is absent
// or not a plain integer — an HTTP-date form of the header is treated the
// same as missing, since the caller only needs a wait duration.
const DEFAULT_UPLOAD_RETRY_AFTER_SECONDS = 20;

function looksLikeHtml(input: string): boolean {
    return /<\/?[a-z][\s\S]*>/i.test(input) || /^\s*<!doctype/i.test(input);
}

function stripHtmlTags(input: string): string {
    return input.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

/**
 * Turns a non-JSON error body (e.g. an HTML error page from a proxy or load
 * balancer) into something safe to show a user: short, tag-free, and a
 * generic fallback when the body is markup/garbage rather than real prose.
 */
function summarizeTextBody(rawText: string, status: number): string {
    const trimmed = rawText.trim();
    if (!trimmed || looksLikeHtml(trimmed)) {
        return `${GENERIC_ERROR_PREFIX} ${status}.`;
    }

    const truncated = trimmed.length > MAX_ERROR_BODY_LENGTH
        ? `${trimmed.slice(0, MAX_ERROR_BODY_LENGTH).trim()}…`
        : trimmed;

    return stripHtmlTags(truncated) || `${GENERIC_ERROR_PREFIX} ${status}.`;
}

function extractErrorMessage(rawText: string, isJson: boolean, status: number): string {
    if (isJson && rawText.trim()) {
        try {
            const payload: any = JSON.parse(rawText);
            const message = typeof payload === "string"
                ? payload
                : payload?.detail ?? payload?.message ?? payload?.error ?? payload?.title;
            if (message) return String(message);
        } catch {
            // Malformed JSON error body; fall through to the generic text handling below.
        }
    }

    return summarizeTextBody(rawText, status);
}

// Thrown by uploadKnowledgeDocument and the direct-upload transport's own
// control-plane calls (initiate, complete), in place of the generic Error
// every other endpoint's failures raise — a 429 is a "wait and resend"
// signal, not a terminal failure, and the sequential upload loop needs
// retryAfterSeconds to act on that distinction instead of parsing the
// message string back apart. Deliberately NOT thrown by uploadDirectPart:
// a part PUT's own bounded retry lives in the caller, one part at a time,
// and does not wait out a shared per-IP window the way a whole file's
// upload does.
export class RateLimitedError extends Error {
    readonly retryAfterSeconds: number;

    constructor(retryAfterSeconds: number, message: string) {
        super(message);
        this.name = "RateLimitedError";
        this.retryAfterSeconds = retryAfterSeconds;
    }
}

function parseRetryAfterSeconds(response: Response): number {
    const header = response.headers.get("Retry-After");
    if (!header) return DEFAULT_UPLOAD_RETRY_AFTER_SECONDS;

    const parsed = Number(header.trim());
    return Number.isFinite(parsed) && parsed > 0 ? parsed : DEFAULT_UPLOAD_RETRY_AFTER_SECONDS;
}

// Shared by every caller that wants a 429 to surface as RateLimitedError
// instead of the generic Error parseResponse would raise for it. Reads the
// body and throws before the caller ever hands the response to
// parseResponse, so the two never race to consume the same response stream.
async function throwIfRateLimited(response: Response): Promise<void> {
    if (response.status !== 429) return;

    const retryAfterSeconds = parseRetryAfterSeconds(response);
    const contentType = response.headers.get("content-type") || "";
    const rawText = await response.text().catch(() => "");
    const message = extractErrorMessage(rawText, contentType.includes("application/json"), response.status);
    throw new RateLimitedError(retryAfterSeconds, message);
}

async function parseResponse<T>(response: Response): Promise<T> {
    const contentType = response.headers.get("content-type") || "";
    const isJson = contentType.includes("application/json");
    const rawText = await response.text().catch(() => "");

    if (!response.ok) {
        throw new Error(extractErrorMessage(rawText, isJson, response.status));
    }

    if (!rawText) {
        // Some endpoints (e.g. feedback) intentionally return an empty success body.
        return {} as T;
    }

    if (!isJson) {
        throw new Error(`${GENERIC_ERROR_PREFIX} ${response.status}.`);
    }

    try {
        return JSON.parse(rawText) as T;
    } catch {
        throw new Error("The service returned an unexpected response. Please try again.");
    }
}

async function fetchWithTimeout(url: string, init: RequestInit, timeoutMs: number): Promise<Response> {
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs);

    try {
        return await fetch(url, { ...init, signal: controller.signal });
    } catch (error) {
        if (controller.signal.aborted) {
            throw new Error("The request took too long to complete. Please try again.");
        }
        throw error;
    } finally {
        window.clearTimeout(timeoutId);
    }
}

async function requestJson<T>(path: string, init: RequestInit, timeoutMs: number): Promise<T> {
    const response = await fetchWithTimeout(apiUrl(path), init, timeoutMs);
    return parseResponse<T>(response);
}

export async function sendChatMessage(message: string, conversationId: string): Promise<ChatResponse> {
    return requestJson<ChatResponse>("/agent/chat", {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
        },
        body: JSON.stringify({ message, conversationId }),
    }, CHAT_TIMEOUT_MS);
}

export async function uploadKnowledgeDocument(file: File, conversationId: string): Promise<UploadResponse> {
    const formData = new FormData();
    formData.append("File", file);
    formData.append("ConversationId", conversationId);

    const response = await fetchWithTimeout(apiUrl("/storage/upload"), {
        method: "POST",
        body: formData,
    }, UPLOAD_TIMEOUT_MS);

    // Diverted before the shared error-message path: every other endpoint's
    // failures become a plain Error, but a 429 here carries a wait duration
    // the caller needs, not just a message to display.
    await throwIfRateLimited(response);

    return parseResponse<UploadResponse>(response);
}

/**
 * Opens a transfer for a file too large for the legacy single-request path. THE DOOR IS THE SAME
 * DOOR — this only differs from uploadKnowledgeDocument in how the bytes move afterwards, not in
 * what is allowed through.
 */
export async function initiateDirectUpload(
    conversationId: string,
    fileName: string,
    sizeBytes: number,
): Promise<InitiateDirectUploadResponse> {
    const response = await fetchWithTimeout(apiUrl("/storage/documents/direct-uploads"), {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
        },
        body: JSON.stringify({ conversationId, fileName, sizeBytes }),
    }, DIRECT_UPLOAD_CONTROL_TIMEOUT_MS);

    await throwIfRateLimited(response);

    return parseResponse<InitiateDirectUploadResponse>(response);
}

/**
 * Uploads one part of a large file. `partUrl` is the branch: present, it PUTs straight to that
 * presigned S3 destination; null, it PUTs to this API's own part endpoint instead (the local
 * provider). The two destinations get deliberately different request shapes — a presigned URL is a
 * bearer credential computed over a specific request, so nothing rides along that was not part of
 * what was signed: no credentials, no custom headers, nothing but the verb, the URL, and the bytes.
 * The local endpoint, by contrast, is this API's own door and behaves like every other call to it.
 *
 * Retrying a failed part is the CALLER's job, one part at a time — this function makes exactly one
 * attempt and reports exactly what happened, never a RateLimitedError: a single part's failure is
 * not the "wait out a shared window" signal the whole-file endpoints raise it for.
 */
export async function uploadDirectPart(
    uploadId: string,
    partNumber: number,
    partUrl: string | null,
    body: Blob,
): Promise<void> {
    const response = partUrl
        ? await fetchWithTimeout(partUrl, { method: "PUT", body }, DIRECT_UPLOAD_PART_TIMEOUT_MS)
        : await fetchWithTimeout(
            apiUrl(`/storage/documents/direct-uploads/${encodeURIComponent(uploadId)}/parts/${partNumber}`),
            { method: "PUT", body },
            DIRECT_UPLOAD_PART_TIMEOUT_MS,
        );

    if (!response.ok) {
        const contentType = response.headers.get("content-type") || "";
        const rawText = await response.text().catch(() => "");
        throw new Error(extractErrorMessage(rawText, contentType.includes("application/json"), response.status));
    }
}

/**
 * Turns a finished transfer into a queued ingestion job. IDEMPOTENT on the backend — a repeat call
 * (e.g. after this request's own timeout fired while the first attempt was still landing) answers
 * with the same jobId rather than re-reading the bytes, so retrying this call is always safe.
 */
export async function completeDirectUpload(uploadId: string): Promise<CompleteDirectUploadResponse> {
    const response = await fetchWithTimeout(
        apiUrl(`/storage/documents/direct-uploads/${encodeURIComponent(uploadId)}/complete`),
        { method: "POST", headers: { Accept: "application/json" } },
        DIRECT_UPLOAD_CONTROL_TIMEOUT_MS,
    );

    await throwIfRateLimited(response);

    return parseResponse<CompleteDirectUploadResponse>(response);
}

/**
 * Tells the backend a transfer will never complete, so it can release whatever the provider is
 * holding for it. Callers treat this as best effort (they have already decided the upload failed;
 * an abort that could not be delivered must not become a second, different failure) — the function
 * itself still surfaces a normal Error on failure, same as every other endpoint, so a caller that
 * does want to know can.
 */
export async function abortDirectUpload(uploadId: string): Promise<void> {
    await requestJson<void>(
        `/storage/documents/direct-uploads/${encodeURIComponent(uploadId)}/abort`,
        { method: "POST", headers: { Accept: "application/json" } },
        DIRECT_UPLOAD_CONTROL_TIMEOUT_MS,
    );
}

/**
 * The other half of the asynchronous upload contract: what became of every ingestion job this
 * conversation has enqueued, newest first. Callers reconcile by `jobId` — a job the response no
 * longer lists (the retention sweep removed it) is not represented as an error here, it is simply
 * absent from the array.
 */
export async function fetchIngestionStatus(conversationId: string): Promise<IngestionJob[]> {
    const query = `conversationId=${encodeURIComponent(conversationId)}`;
    const result = await requestJson<IngestionStatusResponse>(
        `/storage/documents/status?${query}`,
        { method: "GET", headers: { Accept: "application/json" } },
        STATUS_TIMEOUT_MS,
    );
    return result.jobs;
}

export async function clearKnowledgeSession(conversationId: string): Promise<ClearResponse> {
    return requestJson<ClearResponse>("/storage/clear", {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
        },
        body: JSON.stringify({ conversationId }),
    }, CLEAR_TIMEOUT_MS);
}

/**
 * URL of the original uploaded file behind a citation. Used both for
 * fetching the preview blob and as a plain download href for formats with
 * no in-browser viewer.
 */
export function buildDocumentFileUrl(documentId: string, conversationId: string): string {
    const query = `conversationId=${encodeURIComponent(conversationId)}`;
    return apiUrl(`/storage/documents/${encodeURIComponent(documentId)}/file?${query}`);
}

// What fetchDocumentFile resolves with. "oversized" carries no blob at all —
// the whole point of that outcome is that this Blob was never fetched — so
// the drawer's existing cited-passages + download-link fallback is what
// renders for it, exactly as it already does for a format with no
// in-browser viewer.
export type DocumentFilePreviewResult =
    | { status: "ready"; blob: Blob; contentType: string }
    | { status: "oversized" };

/**
 * Fetches the original document behind a citation as a Blob. 404 is a
 * legitimate outcome (a clear or re-upload can race the preview) and
 * surfaces as a normal Error via the shared error-body handling.
 *
 * A HEAD probe runs first, purely to read Content-Length before committing
 * to holding the whole file in memory as a Blob: a document over
 * MAX_PREVIEW_FETCH_BYTES answers "oversized" without ever issuing the GET.
 * The probe FAILS OPEN — no Content-Length, a non-2xx HEAD, a network error,
 * anything short of a confirmed oversized reading falls through to the
 * normal GET below, because a backend or proxy that mishandles HEAD must
 * never be the reason a preview that would otherwise have worked doesn't.
 */
export async function fetchDocumentFile(
    documentId: string,
    conversationId: string,
): Promise<DocumentFilePreviewResult> {
    const url = buildDocumentFileUrl(documentId, conversationId);

    try {
        const headResponse = await fetchWithTimeout(url, { method: "HEAD" }, PREVIEW_HEAD_TIMEOUT_MS);
        if (headResponse.ok) {
            const contentLength = Number(headResponse.headers.get("content-length"));
            if (Number.isFinite(contentLength) && contentLength > MAX_PREVIEW_FETCH_BYTES) {
                return { status: "oversized" };
            }
        }
    } catch {
        // Fail open — see this function's own note above.
    }

    const response = await fetchWithTimeout(url, { method: "GET" }, PREVIEW_TIMEOUT_MS);

    if (!response.ok) {
        const contentType = response.headers.get("content-type") || "";
        const rawText = await response.text().catch(() => "");
        throw new Error(extractErrorMessage(rawText, contentType.includes("application/json"), response.status));
    }

    const blob = await response.blob();
    return { status: "ready", blob, contentType: response.headers.get("content-type") || "" };
}

export async function sendMessageFeedback(
    messageId: string,
    conversationId: string,
    rating: FeedbackRating,
    comment?: string,
): Promise<void> {
    await requestJson<void>("/agent/feedback", {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
        },
        body: JSON.stringify({ messageId, conversationId, rating, comment }),
    }, FEEDBACK_TIMEOUT_MS);
}
